import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import api from "../../../config/URL";
import "../../../styles/admin.css";
import { FiAlertTriangle } from "react-icons/fi";

function PayoutTransactionEdit() {
  const location = useLocation();
  const outstandingBalance = location.state?.outstanding_balance;
  const { id } = useParams();
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
   amount: Yup.number()
    .typeError("*Amount must be a number")
    .required("*Amount is required")
    .min(1, "*Amount must be at least 1")
    .max(outstandingBalance || 0, `*Amount cannot exceed ${outstandingBalance}`),
  });

  const formik = useFormik({
    initialValues: {
      amount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const response = await api.put(
          `affiliate/payout/transaction/${id}`,
          values
        );
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          navigate("/payout-transaction");
        } else if (response.status === 422) {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response) {
          const { status, data } = error.response;

          if (status === 422 && data.errors) {
            Object.keys(data.errors).forEach((key) => {
              data.errors[key].forEach((msg) => {
                toast.error(msg, {
                  icon: <FiAlertTriangle className="text-warning" />,
                });
              });
            });
          } else if (data.message) {
            toast.error(data.message, {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          } else {
            toast.error("An unexpected error occurred.", {
              icon: <FiAlertTriangle className="text-danger" />,
            });
          }
        } else {
          toast.error("Network or server error.", {
            icon: <FiAlertTriangle className="text-danger" />,
          });
        }
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`affiliate/payout/transaction/${id}`);
        const transactionData = response.data.data;

        formik.setValues({
          amount: transactionData.amount || "",
        });
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Error Fetching Data";
        toast.error(errorMessage);
      }
      setLoading(false);
    };
    getData();
  }, [id]);

  return (
    <section className="px-4">
      <form onSubmit={formik.handleSubmit}>
        {loading ? (
          <div className="loader-container">
            <div className="loader">
              <svg viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32"></circle>
              </svg>
            </div>
          </div>
        ) : (
          <>
            <div className="card shadow border-0 mb-3">
              <div className="row p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h1 className="h4 ls-tight">
                    Edit Payout {outstandingBalance}
                  </h1>
                  <div className="d-flex gap-3">
                    {/* Submit */}
                    <div className="">
                      <button
                        type="submit"
                        className="btn btn-sm btn-button"
                        disabled={loadIndicator}
                      >
                        {loadIndicator && (
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            aria-hidden="true"
                          ></span>
                        )}
                        Update Payout
                      </button>
                    </div>
                    <Link to="/payout-transaction">
                      <button type="button" className="btn btn-light btn-sm">
                        <span>Back</span>
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="container card shadow border-0"
              style={{ minHeight: "60vh" }}
            >
              <div className="row mt-3 mb-5 p-3">
                {/* Amount */}
                <div className="col-md-6 col-12 mb-3">
                  <label className="form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="amount"
                    max={outstandingBalance}
                    className={`form-control ${
                      formik.touched.amount && formik.errors.amount
                        ? "is-invalid"
                        : ""
                    }`}
                    value={formik.values.amount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Enter amount"
                  />
                  {formik.touched.amount && formik.errors.amount && (
                    <div className="invalid-feedback">
                      {formik.errors.amount}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </form>
    </section>
  );
}

export default PayoutTransactionEdit;
